import React, { useState } from "react";
import dayjs from "dayjs";
import classNames from "classnames";
import DateTimeBottomPicker from "../../../../components/DateTimeBottomPicker";
import styles from "./index.module.scss";

interface DateTimeFieldProps {
  /** 时间值，格式 "YYYY-MM-DD HH:mm" */
  value?: string;
  onChange?: (value: string) => void;
  /** 左侧字段 label，默认「选择时间」 */
  label?: string;
  /** 右侧占位文案，默认「请选择」 */
  placeholder?: string;
  /** 选择器弹层标题，默认「选择时间」 */
  pickerTitle?: string;
  /** 允许时/分选「未知」（未知展示为「N时」/「时间未知」，值以 "--" 标记） */
  allowUnknown?: boolean;
  /** 只读态：仅展示时间文本，不可点击、不弹选择器、不显示右侧箭头 */
  readOnly?: boolean;
  /** 必填标记：在 label 后展示红色 * （#FF4D4F），仅编辑态生效 */
  required?: boolean;
  /** 校验未通过时标红提示 */
  error?: boolean;
}

const FMT = "YYYY-MM-DD HH:mm";
const UNKNOWN = "--";

// 展示归一化：兼容未知标记
// - "YYYY-MM-DD --:--" → "YYYY-MM-DD 时间未知"
// - "YYYY-MM-DD HH:--" → "YYYY-MM-DD HH时"（省略分钟，暗示分钟未知）
// - 其它合法值 → "YYYY-MM-DD HH:mm"
const formatDisplay = (v?: string) => {
  if (!v) return "";
  const [d = "", t = ""] = v.split(" ");
  const [h = "", m = ""] = t.split(":");
  if (h === UNKNOWN) return `${d} 时间未知`;
  if (m === UNKNOWN) return `${d} ${h}时`;
  return dayjs(v).isValid() ? dayjs(v).format(FMT) : v;
};

/**
 * 通用日期时间选择字段（选填，颗粒度到分钟，不得晚于当前时间）。
 * 纯受控：label / value / onChange 由调用方决定，不绑定任何具体业务语义。
 * 选择器：底部 3 列（日期「M月D日 周X」/ 时 / 分）弹层。
 */
export default function DateTimeField(props: DateTimeFieldProps) {
  const {
    value,
    onChange,
    label = "选择时间",
    placeholder = "请选择",
    pickerTitle = "选择时间",
    allowUnknown = false,
    readOnly = false,
    required = false,
    error = false,
  } = props;
  const [visible, setVisible] = useState(false);

  // 归一化显示：回显格式不一致时统一展示为 YYYY-MM-DD HH:mm，并兼容未知标记
  const displayText = formatDisplay(value);

  return (
    <div className={classNames(styles.field, { [styles.fieldError]: error })}>
      <span className={styles.label}>
        {required && !readOnly && <span className={styles.requiredStar}>*</span>}
        {label}
      </span>
      <div
        className={styles.value}
        onClick={readOnly ? undefined : () => setVisible(true)}
      >
        <span
          className={classNames(styles.text, {
            [styles.placeholder]: !value,
          })}
        >
          {displayText || placeholder}
        </span>
        {!readOnly && <i className={styles.arrow} />}
      </div>

      {!readOnly && (
        <DateTimeBottomPicker
          visible={visible}
          title={pickerTitle}
          value={value}
          allowUnknown={allowUnknown}
          onClose={() => setVisible(false)}
          onConfirm={(v) => onChange?.(v)}
        />
      )}
    </div>
  );
}
